import { ILoggingSettings } from "@multiweb/logging";

/**
 * Konfigurační vlastnosti pro klienta, které mohou být evedeny v souboru initParams.json
 * @includeToDoc
 */
export interface IInitParams {
    /**
     * Název aplikace zobrazovaný v levém horním rohu a na záložce prohlížeče.
     * Výchozí hodnota je prázdný řetězec.
     */
    appName: string;
    /**
     * Kódové označení klientské aplikace.
     * Používá se například pro párování konkrétní klientské aplikace na konfiguraci pro přihlašování na webové službě.
     * Výchozí hodnota je "MW".
     */
    clientCode: string;
    /**
     * Nastavení typu tiskových sestav.
     * Výchozí hodnota je "pdf".
     */
    reportType: string;
    /**
     * Pokud je specifikováno, tak omezuje kořenové uzly hlavního navigačního menu (jsou zobrazeny jen vyjmenované uzly).
     * Týká se atributu Kod položky menu.
     * Výchozí hodnota je `undefined`.
     */
    menuItems?: string[];
    /**
     * Typ použitého FormUI (vzhled toolbaru, formulářového menu a další ovládacích prvků).
     * Pokud je použita vlastní implementace FormUI, tak se zde nastaví její identifikátor a je potřeba ji zaregistrovat pomocí vlastní implementace IFormUIFactory.
     * Výchozí hodnota je "standard".
     */
    formUI: string; //TODO (WI-15673) Odstranit, nebude potřeba
    /**
     * Interval v minutách, po jehož uplynutí je při neaktivitě uživatele volán udržovací dotaz na webovou službu, který zajišťuje, že nevyprší uživatelská session.
     * Udržovací dotaz je ve tvaru "UserRoleIDs?heartBeat=true".
     * Hodnota 0 deaktivuje tuto funkcionalitu.
     * Výchozí hodnota je 0.
     */
    heartBeatInterval: number;
    /**
     * Doba v minutách, po kterou jsou volány udržovací dotazy na webovou službu při souvislé neaktivitě uživatele. Pokud neaktivita překročí tuto dobu, tak se udržovací dotazy přestanou posílat.
     * Hodnota 0 vypíná tento omezovač. Dotazy se budou opakovat do nekonečna.
     * Výchozí hodnota je 0.
     */
    heartBeatTimeout: number;
    /**
     * Seznam externích modulů, které mají být zavedeny.
     */
    dependencies?: IInitParamsDependency[];
    /**
     * Seznam konfigurace pro moduly
     */
    modules?: IInitParamsModulesConfigList;
    /**
     * Pole názvů souborů s CSS styly. Všechny soubory musí být uvnitř klientské složky 'css' nebo v její podsložce.
     * Pokud je hodnotou undefined (nevyplněno, výchozí hodnota), tak se použijí výchozí styly (multiweb.css a Custom.css).
     * Pokud je hodnotou pole, tak se automaticky nezavádí žádné styly, ale pouze styly uvedené v poli a zavádí se v daném pořadí.
     * Je tedy nutné uvést i soubor 'multiweb.css', pokud se má použít.
     * Pokud však máte vlastní soubor(y), tak styl 'multiweb.css' vůbec použít nemusíte a můžete vyjmenovat pouze své soubory se styly.
     * Soubory mohou být i hlouběji zanořeny.
     * Příklad použití: ["multiweb.css", "dashboard/main.css", "dashboard/blue.css", "othercomponent/styles.css"].
     */
    styles?: string[];
    /**
     * Výchozí chování porovnávání řetězců v našeptávači LOV. Může mít hodnotu "startsWith" nebo "contains" (výchozí)
     */
    defaultWhisperer: string;
    /**
     * Výchozí chování porovnávání řetězců ve filtru. Může mít hodnotu "startsWith" (výchozí) nebo "contains"
     */
    defaultFilter: string;
    /**
     * Vývojový režim. Povoluje některá nastavení a funkcionality, které usnadňují vývoj, ale ke kterým by neměl mít přístup běžný uživatel.
     * Na produkčním prostření NESMÍ být zapnuté.
     * Může nabývat boolean hodnot nebo obsahovat strukturu s dalším nastavením. Pokud obsahuje strukturu, tak je automaticky zapnutý.
     * Výchozí hodnota je false.
     */
    developmentMode: boolean | IDevelopmentSettings;

    /**
     * @see ILoggingSettings
     */
    logging: ILoggingSettings;

    /**
     * Interval v sekundách pro logování uživatelských akcí. Pokud není uvedeno nebo je hodnota 0 detekce uživatelských akcí je vypnutá.
     */
    userActionLoggerInterval: number;
    /**
     * Maximalní počet formulářů zobrazených v sekci 'poslední otevřené formuláře'. Výchozí hodnota je 5
     */
    openedFormsHistoryCount: number;
    /**
     * The maximum number of records for objectList defined by ids (created with CTRL+A). Default value is 500.
     */
    forceIdsInSelectAllConditionThreshold: number;
    /**
     * Nastavuje stav rozbalenosti hlavního menu. Výchozí hodnota je false, tedy menu je sbalené.
     */
    showMainMenuExpanded: boolean;
    /**
     * Nastavuje zobrazení popisků položek nejvyšší úrovně hlavního menu. Výchozí hodnota je false, tedy popisky jsou skryté.
     */
    showMainMenuCaptions: boolean;
    /**
     * Příznak, zda se v hlavičkách requestů na server budou posílat všechny aktivní role uživatele dle profilu. Výchozí hodnota je false.
     */
    legacyActiveRoles?: boolean;
    /**
     * Při nastavení této vlastnosti na true bude na všech ReportAction a SeparateDetailFrame implicitně nastavena vlastnost ShowSettings na true,
     * pokud není v definici formuláře explicitně přepsána na false.
     */
    defaultShowSettings?: boolean;
    /**
     * Url adresa webové služby, se kterou má aplikace komunikovat.
     * Může být absolutní, pak se uvádí i se schématem (https://).
     * Nebo může být relativní. Hodnota "." značí, že služba i klient jsou v jednom adresáři, hodnota ".." značí, že služba je o úroveň výše než klient (výchozí chování).
     */
    webServiceUrl: string;
    /**
     * Nastaví doménu pro přihlašování uživatelů. Zadaná doména se při přihlašování skrytě přidá před uživatelské jméno.
     * Výsledný UserName pro doménu 'tescosw.loc' a uživatele 'isum_test_01' bude vypadat takto: 'tescosw.loc\isum_test_01'.
     * Výchozí hodnota je nevyplněno (undefined), kdy se UserName nějak neupravuje.
     */
    defaultDomain?: string;
    /**
     * Počet nejčastějších formulářů, jejich definice se přednačítají při spuštění aplikace.
     * Výchozí hodnota je 10.
     * Hodnota 0 předkešování vypne.
     */
    preloadFormCount: number;

    /**
     * Nastavuje, které experimentální funkcionality mají být aktivní, @see https://www.martinfowler.com/articles/feature-toggles.html Feature Toggles.
     * To umožňuje zkoušet některé funkcionality ještě dříve, než jsou kompletně dokončené a určené pro produkční nasazení.
     * V produkčním prostředí by neměly být aktivní žádné experimenty.
     * Výchozí hodnota je nevyplněno (undefined), tedy žádné experimentální funkcionality nejsou aktivovány.
     */
    experiments?: boolean | readonly ExperimentName[];

    /**
     * Výchozí ikona položek menu
     */
    defaultMenuItemsIcon?: string;
}

/**
 * Názvy experimentálních funkcionalit
 */
export type ExperimentName = "EasterEggs";

/**
 * Interface pro definici externího modulu.
 * @includeToDoc
 */
export interface IInitParamsDependency {
    /**
     * Název modulu, který má být zaveden.
     */
    name: string;
    /**
     * Vygenerovaný soubor s modulem (bez přípony .js) uvnitř klientské složky 'js'.
     */
    file: string;
}

/**
 * Interface pro definici konfigurace modulu
 * @includeToDoc
 */
export interface IInitParamsModulesConfigList {
    pdfEditor?: IInitParamPDFEditorConfig;
    [module: string]: any;
}

/**
 * Definice podrobného nastavení pro vývojový režim. Povoluje některá nastavení a funkcionality, které usnadňují vývoj, ale ke kterým by neměl mít přístup běžný uživatel.
 * Na produkčním prostření NESMÍ být zapnuté.
 * @includeToDoc
 */
export interface IDevelopmentSettings {
    /**
     * Uživatelské jméno, pod kterým se aplikace automaticky přihlásí
     */
    userName?: string;
    /**
     * Uživatelské heslo, pod kterým se aplikace automaticky přihlásí
     */
    userPassword?: string;
    /**
     * Určuje verzi webové služby, na kterou se klientská aplikace napojuje.
     * Je potřeba jen v případě, že se klientská aplikace napojuje na službu starší verze, aby věděla, jak s ní komunikovat.
     * Pouze pro účely vývoje! Veškerá funkcionalita aplikace není zaručena, ale základní funkcionalita by měla fungovat.
     * Verze se zadává jako text ve tvaru XX.YY
     */
    webServiceVersion?: string;
    /**
     * Příznak, zda se má zapnout debug mód vyhodnocování lokálně vyhodnotitelných podmínek.
     * V něm se tyto podmínky posílají k vyhodnocení na server a výsledek se porovnává s lokálním výsledkem.
     */
    debugLocallyEvaluableConditions?: boolean;
}

/**
 * Rozhrani pro konfiguraci modulu PDFEditor
 */
export interface IInitParamPDFEditorConfig {
    previewFileSizeLimit?: number;
}
